import { InjectionToken, Provider } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { MockApiService } from './api-mock.service';
import { CustosEnvironment, CUSTOS_ENVIRONMENT } from '../models/environment';
import {
  ApiMarket,
  ApiMarketPrice,
  ApiCycle,
  ApiOrder,
  ApiOrderSettlement,
  ApiLocker,
  ApiLockerOrder,
  ApiLockerUnlockOrder,
  BetSignatureRequest,
  BetSignatureResponse,
  TransferMessageHashRequest,
  TransferMessageHashResponse,
  ApiTvl,
  ApiTvlHistory,
  ApiPriceHistory,
  ApiTokenInfo,
  StakingRewardPending,
  StakingRewardSnapshot,
  LockerRecord,
  LockerClaim,
  PagedResult,
  MarketQueryParams,
  CycleQueryParams,
  LockerQueryParams,
  LockerListQueryParams,
  LockerOrderQueryParams,
  OrderQueryParams,
  PaginatedQueryParams,
  TableSchemaColumn,
  TableRowsResponse,
  TableRowsParams,
} from './api.types';

export interface CustosApi {
  health(): Observable<{ status: string }>;

  // Markets
  getMarkets(): Observable<ApiMarket[]>;
  getMarket(params: MarketQueryParams): Observable<ApiMarket>;
  getMarketPrice(params: MarketQueryParams): Observable<ApiMarketPrice>;

  // Cycles
  getCycles(params: MarketQueryParams & PaginatedQueryParams): Observable<ApiCycle[]>;
  getCycle(params: CycleQueryParams): Observable<ApiCycle>;

  // Orders
  getOrders(params: OrderQueryParams): Observable<ApiOrder[]>;
  getOrdersByCycle(
    params: CycleQueryParams & PaginatedQueryParams & { user_address?: string },
  ): Observable<ApiOrder[]>;

  // Settlements
  getOrderSettlements(params: OrderQueryParams): Observable<ApiOrderSettlement[]>;

  // Paged variants (preserve hasMore for UI pagination)
  getOrdersPaged(params: OrderQueryParams): Observable<PagedResult<ApiOrder>>;
  getOrderSettlementsPaged(params: OrderQueryParams): Observable<PagedResult<ApiOrderSettlement>>;
  getLockerOrdersPaged(params: LockerOrderQueryParams): Observable<PagedResult<ApiLockerOrder>>;

  // Locker
  getLockers(params: LockerListQueryParams): Observable<ApiLocker[]>;
  getLocker(params: LockerQueryParams): Observable<ApiLocker>;
  getLockerOrders(params: LockerOrderQueryParams): Observable<ApiLockerOrder[]>;
  getLockerUnlockOrders(params: LockerOrderQueryParams): Observable<ApiLockerUnlockOrder[]>;

  // Signatures
  createBetSignature(body: BetSignatureRequest): Observable<BetSignatureResponse>;
  createTransferMessageHash(
    body: TransferMessageHashRequest,
  ): Observable<TransferMessageHashResponse>;

  // TVL
  getTvl(): Observable<ApiTvl>;
  getTvlHistory(range: string): Observable<ApiTvlHistory[]>;

  // Price history
  getPriceHistory(marketId: string): Observable<ApiPriceHistory[]>;

  // Token info
  getTokenInfo(tokenSymbol: string): Observable<ApiTokenInfo>;

  // Market price history (real API)
  getMarketPriceHistory(params: { chain_id: number; market_id: string }): Observable<{ price: number; captured_at: string; timestamp?: string }[]>;

  // Locker price (real API)
  getLockerPrice(params: { chain_id: number; locker_id: string }): Observable<{ price: number }>;

  // TVL (real API)
  getTotalTvl(): Observable<{ time: string; usd_value: number }[]>;
  getTrustLockerTvl(): Observable<{ time: string; usd_value: number }[]>;
  getRollLockerTvl(): Observable<{ time: string; usd_value: number }[]>;

  // Staking rewards
  getStakingRewardsPending(params: { user_address: string }): Observable<StakingRewardPending[]>;
  getStakingRewardsHistory(params: { user_address: string }): Observable<StakingRewardSnapshot[]>;

  // Locker reward distribution tables (Guy backend 2026-04)
  getLockerRecords(params: { limit?: number; user_address?: string }): Observable<LockerRecord[]>;
  getLockerClaims(params: { limit?: number; locker_id?: string }): Observable<LockerClaim[]>;

  // Admin/debug
  getTables(): Observable<{ tables: string[] }>;
  getTableSchema(
    tableName: string,
  ): Observable<{ table: string; columns: TableSchemaColumn[] }>;
  getTableCount(tableName: string): Observable<{ table: string; count: number }>;
  getTableRows(tableName: string, params?: TableRowsParams): Observable<TableRowsResponse>;
}

export const API_SERVICE = new InjectionToken<CustosApi>('API_SERVICE');

export function provideApiService(): Provider[] {
  return [
    ApiService,
    MockApiService,
    {
      provide: API_SERVICE,
      useFactory: (
        env: CustosEnvironment,
        real: ApiService,
        mock: MockApiService,
      ): CustosApi => (env.useMockApi ? mock : real),
      deps: [CUSTOS_ENVIRONMENT, ApiService, MockApiService],
    },
  ];
}
